'use client';

import { ArcElement, Chart as ChartJS, ChartOptions, Legend, Tooltip } from 'chart.js';
import { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

// Register chart.js components (ArcElement, Tooltip, Legend) for use in the chart
ChartJS.register(ArcElement, Tooltip, Legend);

interface RsvpChartProps {
  playingPlayers: number | undefined;
  notPlayingPlayers: number | undefined;
  waitlistPlayers: number | undefined;
  isWaitlist: boolean;
}

// Component for rendering a placeholder legend item
function LegendPlaceholderItem() {
  return (
    <div className='flex'>
      <div className='size-3 rounded-full bg-leagues-300/10' />
      <div className='ml-2 h-3 w-[80px] rounded-lg bg-leagues-300/10' />
    </div>
  );
}

function RsvpChartLoader() {
  return (
    <div className='flex items-center justify-center space-x-1 py-[70px]'>
      {/* Circle loader animation */}
      <div className='size-40 animate-pulse rounded-full border-[36px] border-leagues-300/10'></div>
      <div className='flex flex-col'>
        <div className='flex flex-col gap-0.5 py-4'>
          <div className='flex items-center justify-start'>
            {/* Placeholder items mimicking the layout of the legend */}
            <div className='flex animate-pulse flex-col gap-2.5'>
              <LegendPlaceholderItem />
              <LegendPlaceholderItem />
              <LegendPlaceholderItem />
              <LegendPlaceholderItem />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RsvpChart({
  playingPlayers,
  notPlayingPlayers,
  waitlistPlayers,
  isWaitlist,
}: RsvpChartProps) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate data fetching or processing time by setting a timeout
    const timer = setTimeout(() => {
      setLoading(false); // Set loading to false once the chart is ready
    }, 1000);

    return () => clearTimeout(timer); // Cleanup the timer on component unmount
  }, []);

  // Options for customizing the chart's appearance
  const options = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 10,
          font: {
            size: 14,
          },
          boxWidth: 10,
          boxHeight: 10,
        },
      },
    },
  };

  let labels = []; // Array to hold the labels for the chart
  let data = []; // Array to hold the data points for the chart
  let backgroundColor = []; // Array to hold the colors for each segment of the chart

  // If the waitlist is active for specific league, include waitlist players in the chart data
  if (isWaitlist === true) {
    labels = [
      ` ${playingPlayers} Playing`,
      ` ${notPlayingPlayers} Not Playing`,
      ` ${waitlistPlayers} Waitlist`,
      ` ${(playingPlayers ? playingPlayers : 0) + (waitlistPlayers ? waitlistPlayers : 0) + (notPlayingPlayers ? notPlayingPlayers : 0)} Total`,
    ];
    data = [playingPlayers, notPlayingPlayers, waitlistPlayers];
    backgroundColor = ['#C53153', '#F63D68', '#F63D6833', '#E1E1E1']; // Color scheme for the chart (playing list, not playing list, wait list, total players)
  } else {
    // If the waitlist is not active, only include playing, not playing and total players
    labels = [
      ` ${playingPlayers} Playing`,
      ` ${notPlayingPlayers} Not Playing`,
      ` ${(playingPlayers ? playingPlayers : 0) + (notPlayingPlayers ? notPlayingPlayers : 0)} Total`,
    ];
    data = [playingPlayers, notPlayingPlayers];
    backgroundColor = ['#C53153', '#F63D68', '#E1E1E1']; // Color scheme for the chart (playing list, not playing list, total players)
  }

  // Data object for the Doughnut chart
  const chartData = {
    labels,
    datasets: [
      {
        label: '# of Players',
        data,
        backgroundColor,
        font: {
          size: 24, // Font size for the labels inside the chart
        },
      },
    ],
  };

  return (
    <div className='flex w-full items-center justify-center'>
      <div className='flex items-center justify-center'>
        {loading ? (
          <RsvpChartLoader />
        ) : (
          <Doughnut data={chartData} options={options as ChartOptions<'doughnut'>} />
        )}
      </div>
    </div>
  );
}
